import readLocalization from './readLocalization';
import withSessionStorage from './withSessionStorage';

const DEFAULT_MAX_ENTRIES = 50;
const STORAGE_KEY_PREFIX = 'redesign.resourceCache.';
const STORAGE_KEY_SEPARATOR = '|';

function hasOutlivedTtl(entry, ttlMs, now) {
    return now - entry.storedAt >= ttlMs;
}

function createResourceCache({ ttlMs, maxEntries = DEFAULT_MAX_ENTRIES } = {}) {
    if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
        throw new Error('createResourceCache requires a positive ttlMs');
    }

    if (!Number.isInteger(maxEntries) || maxEntries <= 0) {
        throw new Error('createResourceCache requires a positive integer maxEntries');
    }

    const entries = new Map();

    function dropOldestEntry() {
        entries.delete(entries.keys().next().value);
    }

    function peek(key) {
        const entry = entries.get(key);

        return entry === undefined || hasOutlivedTtl(entry, ttlMs, Date.now()) ? null : { data: entry.data };
    }

    function read(key) {
        const entry = entries.get(key);

        if (entry === undefined) {
            return null;
        }

        if (hasOutlivedTtl(entry, ttlMs, Date.now())) {
            entries.delete(key);
            return null;
        }

        return { data: entry.data };
    }

    function write(key, data) {
        entries.delete(key);
        entries.set(key, { data, storedAt: Date.now() });

        if (entries.size > maxEntries) {
            dropOldestEntry();
        }
    }

    return { peek, read, write };
}

const persistedEntries = new Map();

function readLocalizationScope() {
    const { locale, country } = readLocalization();

    return `${locale}-${country}`;
}

function buildStorageKey(namespace, key) {
    return [`${STORAGE_KEY_PREFIX}${readLocalizationScope()}`, namespace, key].join(STORAGE_KEY_SEPARATOR);
}

function readStorageKeyNamespace(storageKey) {
    return storageKey.startsWith(STORAGE_KEY_PREFIX) ? storageKey.split(STORAGE_KEY_SEPARATOR)[1] : null;
}

function listStorageKeys() {
    return withSessionStorage(storage => Object.keys(storage)) ?? [];
}

function readPersistedEntry(storageKey) {
    return withSessionStorage((storage) => {
        const raw = storage.getItem(storageKey);

        return raw === null ? null : JSON.parse(raw);
    });
}

function persistEntry(storageKey, entry) {
    withSessionStorage(storage => storage.setItem(storageKey, JSON.stringify(entry)));
}

function forgetPersistedEntry(storageKey) {
    persistedEntries.delete(storageKey);
    withSessionStorage(storage => storage.removeItem(storageKey));
}

function isUsableEntry(entry) {
    return Boolean(entry) && Number.isFinite(entry.storedAt);
}

function forgetExpiredEntries(namespace, ttlMs, now) {
    new Set([...persistedEntries.keys(), ...listStorageKeys()]).forEach((storageKey) => {
        if (readStorageKeyNamespace(storageKey) !== namespace) {
            return;
        }

        const entry = persistedEntries.get(storageKey) ?? readPersistedEntry(storageKey);

        if (!isUsableEntry(entry) || hasOutlivedTtl(entry, ttlMs, now)) {
            forgetPersistedEntry(storageKey);
        }
    });
}

function createPersistedResourceCache({ namespace, ttlMs }) {
    if (typeof namespace !== 'string' || namespace === '') {
        throw new Error('createPersistedResourceCache requires a non-empty namespace');
    }

    if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
        throw new Error('createPersistedResourceCache requires a positive ttlMs');
    }

    function resolveEntry(key) {
        if (!key) {
            return null;
        }

        const storageKey = buildStorageKey(namespace, key);
        const entry = persistedEntries.get(storageKey) ?? readPersistedEntry(storageKey);

        return isUsableEntry(entry) ? { storageKey, entry } : null;
    }

    function peek(key) {
        const resolved = resolveEntry(key);

        if (resolved === null || hasOutlivedTtl(resolved.entry, ttlMs, Date.now())) {
            return null;
        }

        return { data: resolved.entry.data };
    }

    function read(key) {
        const resolved = resolveEntry(key);

        if (resolved === null) {
            return null;
        }

        if (hasOutlivedTtl(resolved.entry, ttlMs, Date.now())) {
            forgetPersistedEntry(resolved.storageKey);
            return null;
        }

        persistedEntries.set(resolved.storageKey, resolved.entry);

        return { data: resolved.entry.data };
    }

    function write(key, data) {
        if (!key) {
            return;
        }

        const storageKey = buildStorageKey(namespace, key);
        const entry = { data, storedAt: Date.now() };

        forgetExpiredEntries(namespace, ttlMs, entry.storedAt);
        persistedEntries.set(storageKey, entry);
        persistEntry(storageKey, entry);
    }

    return { peek, read, write };
}

function clearCachedResources() {
    persistedEntries.clear();

    listStorageKeys()
        .filter(storageKey => storageKey.startsWith(STORAGE_KEY_PREFIX))
        .forEach(forgetPersistedEntry);
}

export {
    DEFAULT_MAX_ENTRIES,
    clearCachedResources,
    createPersistedResourceCache,
};
export default createResourceCache;
